import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';

import { SelectField, SwitchField, TextField } from '@/components/form-fields';
import {
  EmptyState,
  ListRow,
  ListShell,
  ReadOnlyNotice,
  SectionCard,
  SettingsSkeleton,
} from '@/components/settings/settings-kit';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth';
import { listLicenses, listLocations } from '@/lib/settings/locations';
import {
  clearMetrcCredentials,
  connectMetrcFacility,
  fetchMetrcFacilities,
  getMetrcPackageStatus,
  listMetrcConfigs,
  METRC_STATES,
  type MetrcConfig,
  type MetrcFacility,
  type MetrcSyncProgress,
  runMetrcPackageSync,
} from '@/lib/settings/metrc';

/**
 * Metrc, per licence.
 *
 * Connecting is discovery-driven, in two steps: type the user key and we ask
 * Metrc what it can reach, then pick one of those facilities and the location
 * it belongs to. The merchant never types a vendor key -- that is a platform
 * secret held by the edge function, and it is the reason discovery can happen
 * before any licence row exists.
 *
 * The key is held in component state only long enough to span the two steps,
 * then dropped. It goes to Vault on connect and nothing can read it back --
 * which is why this screen can say a licence is connected and when it last
 * synced, and nothing more.
 *
 * @see docs/metrc.md
 */

export function MetrcSettings({ accountId }: { accountId: string }) {
  const { can } = useAuth();
  const canManage = can('compliance.manage');
  const queryClient = useQueryClient();
  const [connecting, setConnecting] = useState(false);

  const licenses = useQuery({
    queryKey: ['licenses', accountId],
    queryFn: () => listLicenses(accountId),
  });

  const configs = useQuery({
    queryKey: ['metrc-configs', accountId],
    queryFn: () => listMetrcConfigs(accountId),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['metrc-configs', accountId] });
    // A connect upserts a licence row, so the licence list can be stale too.
    queryClient.invalidateQueries({ queryKey: ['licenses', accountId] });
  };

  const disconnect = useMutation({
    mutationFn: (configId: string) => clearMetrcCredentials(accountId, configId),
    onSuccess: () => {
      toast.success('Credentials cleared.');
      refresh();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (licenses.isLoading || configs.isLoading) return <SettingsSkeleton />;

  const connected = configs.data ?? [];

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      {!canManage && <ReadOnlyNotice permission="compliance.manage" />}

      <SectionCard
        title="Track and trace"
        description="Connect a Metrc facility with your user key. Keys are stored in Vault, so this screen can show that a licence is connected but never show the key again."
        action={
          canManage && !connecting ? (
            <Button type="button" size="sm" onClick={() => setConnecting(true)}>
              Connect a facility
            </Button>
          ) : null
        }
      >
        {connected.length === 0 && !connecting ? (
          <EmptyState>
            No Metrc facility is connected yet. Connect one with the user key from the licensee's
            own Metrc account.
          </EmptyState>
        ) : (
          <ListShell>
            {connected.map((config) => {
              const license = licenses.data?.find((row) => row.id === config.licenseId);

              return (
                <ListRow
                  key={config.id}
                  title={license?.licenseNumber ?? facilityName(config) ?? 'Connected facility'}
                  subtitle={connectionSummary(config)}
                  badges={
                    config.enabled ? (
                      <Badge>Connected</Badge>
                    ) : (
                      <Badge variant="outline">Paused</Badge>
                    )
                  }
                  actions={
                    canManage ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => disconnect.mutate(config.id)}
                        disabled={disconnect.isPending}
                      >
                        Disconnect
                      </Button>
                    ) : null
                  }
                />
              );
            })}
          </ListShell>
        )}

        {connected.filter(isSyncable).map((config) => (
          <PackageSync
            key={config.id}
            accountId={accountId}
            config={config}
            canManage={canManage}
          />
        ))}

        {connecting && (
          <MetrcConnect
            accountId={accountId}
            onDone={() => {
              setConnecting(false);
              refresh();
            }}
            onCancel={() => setConnecting(false)}
          />
        )}
      </SectionCard>
    </div>
  );
}

/** The connected facility's display name, if the stored record carries one. */
function facilityName(config: MetrcConfig): string | null {
  const name = config.facility?.DisplayName ?? config.facility?.Name;

  return typeof name === 'string' && name.trim() !== '' ? name : null;
}

function connectionSummary(config: MetrcConfig): string {
  const parts = [config.state, config.sandbox ? 'sandbox' : 'production'];

  const name = facilityName(config);

  if (name) parts.unshift(name);

  if (config.lastVerifiedAt) {
    parts.push(`verified ${new Date(config.lastVerifiedAt).toLocaleDateString()}`);
  }

  if (config.lastError) parts.push(config.lastError);

  return parts.join(' · ');
}

/**
 * Step one: the key. Step two: what it reached.
 *
 * Discovery is a plain mutation rather than a query -- it has a side effect at
 * Metrc's end (an authentication attempt), so it must happen when the merchant
 * says so and never on a refetch.
 */
function MetrcConnect({
  accountId,
  onDone,
  onCancel,
}: {
  accountId: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [state, setState] = useState<string>('CA');
  const [sandbox, setSandbox] = useState(true);
  const [userKey, setUserKey] = useState('');
  const [facilities, setFacilities] = useState<MetrcFacility[] | null>(null);

  const locations = useQuery({
    queryKey: ['locations', accountId],
    queryFn: () => listLocations(accountId),
  });

  const discover = useMutation({
    mutationFn: () => fetchMetrcFacilities({ accountId, userKey: userKey.trim(), state, sandbox }),
    onSuccess: (result) => {
      setFacilities(result);
      toast.success(
        result.length === 1
          ? 'Found 1 facility for that key.'
          : `Found ${result.length} facilities for that key.`,
      );
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const connect = useMutation({
    mutationFn: (input: { facility: MetrcFacility; locationId: string }) =>
      connectMetrcFacility({
        accountId,
        locationId: input.locationId,
        state,
        sandbox,
        userKey: userKey.trim(),
        facility: input.facility,
      }),
    onSuccess: () => {
      toast.success('Facility connected.');
      // Drop the key the moment it is no longer needed.
      setUserKey('');
      setFacilities(null);
      onDone();
    },
    onError: (error: Error) => toast.error(error.message.replace(/^[a-z_]+:\s*/i, '')),
  });

  const activeLocations = (locations.data ?? []).filter((location) => location.active);

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-dashed p-4">
      <form
        onSubmit={(event) => {
          event.preventDefault();

          if (!userKey.trim()) {
            toast.error('Enter your Metrc user key.');
            return;
          }

          discover.mutate();
        }}
        noValidate
        className="flex flex-col gap-4"
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            id="metrc-state"
            label="State"
            description="Which Metrc instance this licence reports to."
            value={state}
            options={METRC_STATES.map((code) => ({ value: code, label: code }))}
            onChange={(next) => {
              setState(next);
              // The old results belong to the old state.
              setFacilities(null);
            }}
          />
          <TextField
            id="metrc-user-key"
            label="User key"
            type="password"
            description="From the licensee's own Metrc account. The vendor key is ours and is never entered here."
            value={userKey}
            onChange={(next) => {
              setUserKey(next);
              setFacilities(null);
            }}
          />
        </div>

        <SwitchField
          id="metrc-sandbox"
          label="Sandbox"
          description="Point at the state's test instance until the numbers are trusted."
          checked={sandbox}
          onChange={(next) => {
            setSandbox(next);
            setFacilities(null);
          }}
        />

        <div className="flex gap-2">
          <Button type="submit" size="sm" disabled={discover.isPending}>
            {discover.isPending ? 'Checking with Metrc…' : 'Find facilities'}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>

      {facilities && (
        <div className="flex flex-col gap-3 border-t pt-4">
          <div>
            <p className="text-sm font-medium">Facilities on this key</p>
            <p className="text-muted-foreground text-xs">
              Choose the location each facility reports for. Connecting stores the key in Vault and
              links the licence to that location.
            </p>
          </div>

          {activeLocations.length === 0 ? (
            <EmptyState>
              This account has no active locations. Add one under Locations before connecting a
              facility.
            </EmptyState>
          ) : (
            <div className="flex flex-col gap-3">
              {facilities.map((facility) => (
                <FacilityCard
                  key={facility.licenseNumber}
                  facility={facility}
                  locations={activeLocations}
                  pending={connect.isPending}
                  onConnect={(locationId) => connect.mutate({ facility, locationId })}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FacilityCard({
  facility,
  locations,
  pending,
  onConnect,
}: {
  facility: MetrcFacility;
  locations: { id: string; name: string }[];
  pending: boolean;
  onConnect: (locationId: string) => void;
}) {
  const [locationId, setLocationId] = useState(locations[0]?.id ?? '');

  return (
    <div className="flex flex-col gap-3 rounded-lg border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">{facility.displayName}</span>
        {facility.isProvisional && <Badge variant="outline">Provisional</Badge>}
        {facility.licenseType && <Badge variant="secondary">{facility.licenseType}</Badge>}
      </div>

      <p className="text-muted-foreground text-xs">
        {facility.licenseNumber}
        {facility.licenseEndDate
          ? ` · expires ${new Date(facility.licenseEndDate).toLocaleDateString()}`
          : ''}
      </p>

      {facility.permissions.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {facility.permissions.map((permission) => (
            <Badge key={permission} variant="outline" className="font-normal">
              {humanizePermission(permission)}
            </Badge>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <SelectField
          id={`metrc-location-${facility.licenseNumber}`}
          label="Location"
          className="min-w-56 flex-1"
          value={locationId}
          options={locations.map((location) => ({ value: location.id, label: location.name }))}
          onChange={setLocationId}
        />
        <Button
          type="button"
          size="sm"
          disabled={pending || !locationId}
          onClick={() => onConnect(locationId)}
        >
          {pending ? 'Connecting…' : 'Connect'}
        </Button>
      </div>
    </div>
  );
}

/**
 * `CanSellToConsumers` -> `Can sell to consumers`.
 *
 * Metrc's permission flags are PascalCase field names and the set differs by
 * state, so they are split rather than mapped -- an unknown flag still reads
 * as a sentence instead of falling through to nothing.
 */
function humanizePermission(name: string): string {
  const words = name.replace(/([a-z0-9])([A-Z])/g, '$1 $2');

  return words.charAt(0).toUpperCase() + words.slice(1).toLowerCase();
}

/**
 * A connection is syncable once Metrc has confirmed the key and a facility has
 * actually been chosen.
 *
 * Both halves matter: `lastVerifiedAt` is stamped by `connect_metrc_facility`,
 * and an empty `facility` means a row exists without a facility behind it --
 * which the older credential flow could produce and which has no licence to
 * query.
 */
function isSyncable(config: MetrcConfig): boolean {
  return Boolean(config.lastVerifiedAt) && Object.keys(config.facility ?? {}).length > 0;
}

const LIST_LABELS: Record<string, string> = {
  active: 'active',
  onhold: 'on-hold',
  inactive: 'finished',
};

/**
 * The package mirror for one connected facility.
 *
 * The sync is a loop of bounded slices, not one request -- the worker can only
 * spend so many subrequests per request, so it walks a few pages and hands back
 * a cursor. `runMetrcPackageSync` drives that loop and reports after each
 * slice, which is what the progress line below is reading. A first sync of two
 * years of history is minutes of work, so it has to count up rather than sit
 * on a spinner.
 */
function PackageSync({
  accountId,
  config,
  canManage,
}: {
  accountId: string;
  config: MetrcConfig;
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<{ imported: number; slice: MetrcSyncProgress } | null>(
    null,
  );

  const status = useQuery({
    queryKey: ['metrc-package-status', accountId],
    queryFn: () => getMetrcPackageStatus(accountId),
  });

  const sync = useMutation({
    mutationFn: () =>
      runMetrcPackageSync(accountId, {
        configId: config.id,
        onProgress: (slice, imported) => setProgress({ imported, slice }),
      }),
    onSuccess: ({ imported }) => {
      toast.success(
        imported === 0
          ? 'No package changes to import.'
          : `Imported ${imported.toLocaleString()} package${imported === 1 ? '' : 's'}.`,
      );
      setProgress(null);
      status.refetch();
      queryClient.invalidateQueries({ queryKey: ['metrc-configs', accountId] });
    },
    onError: (error: Error) => {
      // Whatever was committed before the failure stays committed, and the
      // next run resumes rather than starting over -- say so, or the merchant
      // reasonably assumes the whole sync was lost.
      toast.error(`${error.message} Anything already imported was kept.`);
      setProgress(null);
    },
  });

  const lastSyncedAt = status.data?.lastFullSyncAt ?? config.lastFullSyncAt;
  const packageCount = status.data?.packageCount;

  return (
    <div className="flex flex-col gap-3 rounded-lg border p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium">Packages</p>
          <p className="text-muted-foreground text-xs">
            {lastSyncedAt
              ? `Last synced ${new Date(lastSyncedAt).toLocaleString()}`
              : 'Never synced. The first run reaches back two years.'}
            {typeof packageCount === 'number' &&
              ` · ${packageCount.toLocaleString()} in the mirror`}
          </p>
        </div>

        {canManage && (
          <Button type="button" size="sm" onClick={() => sync.mutate()} disabled={sync.isPending}>
            {sync.isPending ? 'Syncing…' : 'Sync packages'}
          </Button>
        )}
      </div>

      {sync.isPending && progress && (
        <p className="text-muted-foreground text-xs" aria-live="polite">
          {progress.imported.toLocaleString()} imported
          {progress.slice.listType &&
            ` · ${LIST_LABELS[progress.slice.listType] ?? progress.slice.listType}`}
          {progress.slice.totalPages
            ? ` · page ${progress.slice.page ?? 0} of ${progress.slice.totalPages}`
            : ''}
          {progress.slice.retryAfterSeconds
            ? ` · Metrc is throttling us, waiting ${progress.slice.retryAfterSeconds}s`
            : ''}
        </p>
      )}
    </div>
  );
}
